import { useEffect, useState } from 'react'
import { Modal, Alert, btnPrimary, btnSecondary, colors, input } from '../../components/ui'
import { api, ApiError } from '../../services/api'

// medflow_admin-only config card (rendered on the fleet overview): shows and
// edits the global support contact email. Stored server-side so it can change
// without a redeploy; customer self-service pages read the same value.
export function SupportEmailCard() {
  const [email, setEmail] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load(): Promise<void> {
    try {
      const res = await api.get<{ email: string }>('/support-email')
      setEmail(res.data?.email ?? null)
    } catch {
      setEmail(null)
    }
  }

  useEffect(() => {
    load()
  }, [])

  function openEdit(): void {
    setDraft(email ?? '')
    setError(null)
    setEditing(true)
  }

  async function handleSave(): Promise<void> {
    setSaving(true)
    setError(null)
    try {
      const res = await api.patch<{ email: string }>('/admin/support-email', { email: draft.trim() })
      setEmail(res.data?.email ?? draft.trim())
      setEditing(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to save support email')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '0.75rem 1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          minWidth: '220px',
        }}
      >
        <div>
          <div style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '2px' }}>
            Support email
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textHeading }}>{email ?? '—'}</div>
        </div>
        <button
          type="button"
          onClick={openEdit}
          style={{ ...btnSecondary, fontSize: '12px', padding: '0.25rem 0.5rem' }}
        >
          Edit
        </button>
      </div>

      <Modal isOpen={editing} onClose={() => setEditing(false)} title="Edit support email">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {error && <Alert type="error" message={error} onDismiss={() => setError(null)} />}
          <input
            type="email"
            style={input}
            placeholder="support@medflowllc.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button
              type="button"
              disabled={saving || draft.trim().length === 0}
              style={btnPrimary}
              onClick={handleSave}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
